import React, { useEffect, useRef, useState } from 'react'
import Icon from 'blocks.simple/icon/icon'
import DropMenu from 'blocks.simple/dropMenu/dropMenu'
import Dropdown from 'blocks.simple/dropdown/dropdown'
import Button from 'blocks.simple/button/button'
import filesHelpers from 'core/helpers/_files'
import broadcastsHelpers from 'core/helpers/_broadcasts'
import translate from 'core/translate'
import deepEqual from 'fast-deep-equal'
import { cn } from 'ethcss'
import styles from './sidebar.jcss'
import grid from 'blocks.simple/grid/grid.jcss'
import item from 'blocks.simple/item/item.jcss'
import { text as textStyles } from 'theme'
import { Typography } from 'atoms/Typography'
import { getThemeStyleValue } from 'theme/colors'
import { defaultThemeStyles } from 'blocks.simple/icon/icon.theme'
import { CircleButton } from 'atoms/CircleButton'
import { isNotEmptyArray } from 'core/utils/coreUtil'
import Checkbox from 'blocks.simple/checkbox/checkbox'
import { Radio } from 'atoms/Radio'
import { colors } from '../../theme'
import { history } from 'features/routes'
import { CustomScroll } from 'atoms/CustomScroll'
import DotTooltip from '../../blocks.simple/dotTooltip/dotTooltip'
import { Tooltip } from 'atoms/Tooltip'
import { Conflicts } from '../../pages/broadcasts/_add/broadcasts_add.local.methods'
import { SelectFromFileToolbarContent } from '../selectFromFile/selectFromFile'
import { WikiInfo } from '../../blocks.simple/wikiInfo/WikiInfo'
import EditableText from '../../blocks.simple/editableText/editableText'
import EditText from '../../blocks.simple/editText/editText'
import Modal from '../../blocks.simple/modal/modal'
import {
    ISidebarProps,
    SidebarAdditionContent,
    SidebarContentProps,
    SidebarDerivedState,
    SidebarFolder,
    SidebarOption,
    SidebarState,
    SidebarSubmitData,
} from './Sidebar-types'

export type { ISidebarProps } from './Sidebar-types'

const Sidebar = <TOption extends SidebarOption>(p_: ISidebarProps<TOption>) => {
    const contentSide = function (side: string) {
        return [p_.content, p_.customContent].some(function (o) {
            return !!(o && o[side])
        })
    }
    const headerIcon = function (side: string) {
        return !!p_.icon?.[side]
    }
    const leftIcon = p_.icon?.left
    const rightIcon = p_.icon?.right
    const rightPane = p_.content?.right

    return (
        <CustomScroll classNames={styles.customScrollBlock}>
            <div className={styles.sidebarWrapper}>
                {!p_.hideHeader ? (
                    p_.customHeader ? (
                        p_.customHeader
                    ) : (
                        <div
                            className={styles.sidebarHeader}
                            onClick={function (e) {
                                e.stopPropagation()
                            }}
                        >
                            {contentSide('left') && (
                                <div
                                    className={cn(
                                        styles.toggleButton,
                                        p_.selectedSide === 'left' ? styles.selectedSide : '',
                                        contentSide('right') ? '' : styles.full
                                    )}
                                    onClick={function () {
                                        if (p_.selectSidebar) {
                                            p_.selectSidebar('left')
                                        }
                                    }}
                                >
                                    {headerIcon('left') && leftIcon && (
                                        <Icon
                                            size={leftIcon.size || '23'}
                                            type={leftIcon.name || 'info'}
                                            color={p_.selectedSide === 'left' ? colors.white : colors.grey}
                                        />
                                    )}
                                    {!headerIcon('left') && <Icon size="23" type="info" color="white" />}
                                </div>
                            )}
                            {contentSide('right') && (
                                <div
                                    className={cn(
                                        styles.toggleButton,
                                        contentSide('left') && p_.selectedSide === 'right' ? styles.selectedSide : '',
                                        contentSide('left') ? styles.buttonWithBorder : '',
                                        contentSide('left') ? '' : styles.full
                                    )}
                                    onClick={function () {
                                        if (p_.selectSidebar) {
                                            p_.selectSidebar('right')
                                        }
                                    }}
                                >
                                    {headerIcon('right') && rightIcon && (
                                        <Icon
                                            size={rightIcon.size || '23'}
                                            type={rightIcon.name || 'info'}
                                            color={
                                                rightIcon.color ||
                                                getThemeStyleValue('ui', 'icon', 'main') ||
                                                defaultThemeStyles.main
                                            }
                                        />
                                    )}
                                    {!headerIcon('right') && (
                                        <Icon
                                            size="23"
                                            type="info"
                                            color={p_.selectedSide === 'right' ? colors.white : colors.grey}
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    )
                ) : null}
                <Content {...p_} side={p_.selectedSide} />
                {rightPane && rightPane.options.length ? (
                    <div className={styles.deselectBtnWrapper}>
                        <Button
                            mod={'withBorder'}
                            type={'button'}
                            onClick={() => {
                                if (p_.onChange) {
                                    p_.onChange([], 'right')
                                }
                            }}
                        >
                            {translate('deselect')}
                        </Button>
                    </div>
                ) : (
                    ''
                )}
            </div>
        </CustomScroll>
    )
}

Sidebar.defaultProps = {
    isShowActions: false,
    isEditable: false,
    isDeletable: false,
    isDownloadable: false,
    onGetFoldersOnMultiple: () => {},
}

const getEditTitle = <TOption extends SidebarOption>(p_: SidebarContentProps<TOption>) => {
    return p_.content?.[p_.side]?.editTitle
}

const isAdditionContentExist = <TOption extends SidebarOption>(p_: SidebarContentProps<TOption>) => {
    const additionContent = p_.content?.[p_.side]?.additionContent

    return !(!additionContent || !isNotEmptyArray(additionContent))
}

const isFolderChanged = (s_: { data: SidebarSubmitData }) => {
    let currentFolderId = s_.data.currentFolderId
    let newFolderId = s_.data.newFolderId

    return currentFolderId && newFolderId && currentFolderId !== newFolderId
}

const getAdditionContentData = <TOption extends SidebarOption>(p_: SidebarContentProps<TOption>): SidebarSubmitData => {
    let data: SidebarSubmitData = {}

    if (!isAdditionContentExist(p_)) return data

    p_.content?.[p_.side]?.additionContent?.forEach(({ name, initValue }) => {
        data = { ...data, [name]: initValue }
    })

    return data
}

const getFileData = <TOption extends SidebarOption>(p_: SidebarContentProps<TOption>): SidebarSubmitData => {
    let sourceIdArray: number[] = []
    let foldersIdArray: number[] = []
    let data: SidebarSubmitData = {}

    const pane = p_.content?.[p_.side]
    if (pane) {
        pane.options.forEach((item) => {
            if (!item || typeof item.id !== 'number') return

            if (pane.isDirectory && pane.isDirectory(item)) {
                foldersIdArray.push(item.id)
            } else {
                sourceIdArray.push(item.id)
            }
        })
        data = {
            sourceId: sourceIdArray,
            folderId: foldersIdArray,
            currentFolderId: Number(pane.folderId),
            newFolderId: Number(pane.folderId),
        }
    }
    return data
}

const getFoldersList = <TOption extends SidebarOption>(p_: SidebarContentProps<TOption>): SidebarFolder[] => {
    let folders: SidebarFolder[] = []
    const pane = p_.content?.[p_.side]
    if (pane) {
        folders = pane.folders || []
        pane.options.forEach((option) => {
            if (!option || typeof option.id !== 'number') return

            if (pane.isDirectory && pane.isDirectory(option)) {
                folders = folders.filter((folder) => folder.id !== option.id)
            }
        })
    }
    return folders
}

const getState = <TOption extends SidebarOption>(p_: SidebarContentProps<TOption>): SidebarDerivedState => {
    const additionContentData = getAdditionContentData(p_)
    const fileData = getFileData(p_)
    const mergedData = { ...additionContentData, ...fileData }

    return {
        data: mergedData,
        folders: getFoldersList(p_),
        editTitle: getEditTitle(p_),
    }
}

const Content = <TOption extends SidebarOption>(p_: SidebarContentProps<TOption>) => {
    const [state, setState] = useState<SidebarState>({
        active: [],
        data: {},
        folders: [],
        visible: false,
        index: 0,
        search: '',
    })
    const pane = p_.content?.[p_.side]
    const paneOptions = pane ? pane.options.filter((option): option is TOption => option !== null) : []
    const conflicts = p_.conflicts || []
    const isEmpty = p_.isEmpty[p_.side]
    const emptyText = p_.emptyText?.[p_.side]
    const searchValue = p_.searchValue !== undefined ? p_.searchValue : state.search
    const useLocalSearch = p_.localSearch !== false
    const showSearch = Boolean(paneOptions.length || p_.onSearchChange)

    useEffect(() => {
        if (p_.moveItemRef) {
            p_.moveItemRef.current = () => {
                setState((prevState) => {
                    return {
                        ...prevState,
                        active: ['edit'],
                    }
                })
            }
        }
        if (p_.deleteItemRef) {
            p_.deleteItemRef.current = () => {
                setState((prevState) => {
                    return {
                        ...prevState,
                        active: ['delete'],
                    }
                })
            }
        }
        if (p_.downloadItemRef) {
            p_.downloadItemRef.current = () => {
                setState((prevState) => {
                    return {
                        ...prevState,
                        active: ['download'],
                    }
                })
            }
        }
    }, [])

    const usePrevious = <T,>(value: T): T | undefined => {
        const ref = useRef<T>()
        useEffect(() => {
            ref.current = value
        })
        return ref.current
    }

    let prevPropsHook = usePrevious(p_)

    useEffect(() => {
        if (!p_.isEditable || !prevPropsHook) {
            return
        }

        const prevData = getState(prevPropsHook)
        const nextData = getState(p_)

        if (!deepEqual(prevData, nextData)) {
            setState((prevState) => {
                return { ...prevState, ...nextData }
            })
        }

        prevPropsHook = p_
    }, [p_])

    const cancelRemove = () => {
        setState((prevState) => {
            return { ...prevState, active: [] }
        })
    }

    const getIcon = (sidebarItem: SidebarOption, conflicts: Conflicts[]) => {
        let icon = 'group_files'

        if (sidebarItem.fileType) {
            icon = filesHelpers.getFileIconName(sidebarItem)
        }
        if (sidebarItem.type === 'group' || sidebarItem.isDirectory || broadcastsHelpers.isDirectory(sidebarItem)) {
            icon = 'files'
        }
        if (sidebarItem.type === 'schedule') {
            icon = 'date'
        }
        const hasConflict = conflicts.some((conflict) => conflict.obj.id === sidebarItem.id)

        if (hasConflict) {
            return <Icon type="attention" color="error" />
        } else {
            return <Icon type={icon} />
        }
    }

    const moveItem = () => {
        return {
            id: 'edit',
            icon: 'move',
            tooltip: {
                title: translate('move'),
            },
            children: renderMoveForm(),
        }
    }

    const deleteItem = () => {
        return {
            id: 'delete',
            icon: 'delete',
            tooltip: {
                title: translate('delete'),
            },
            children: renderDeleteForm(),
        }
    }

    const downloadItem = () => {
        return {
            id: 'download',
            icon: 'download',
            tooltip: {
                title: translate('download'),
            },
            children: renderDownloadForm(),
        }
    }

    interface ItemActionsOptions {
        isEditable?: boolean
        isDownloadable?: boolean
        isDeletable?: boolean
    }

    const getItemActions = (options: ItemActionsOptions = {}) => {
        const actions = []
        if (options.isEditable) {
            actions.push(moveItem())
        }

        if (options.isDeletable) {
            actions.push(deleteItem())
        }

        if (options.isDownloadable) {
            actions.push(downloadItem())
        }

        return actions
    }

    const topListItemClick = (listItem: TOption) => {
        if (p_.onSelectInfo) {
            p_.onSelectInfo(listItem)
        }
    }

    const s_ = state
    const listItemSide = function (side: string) {
        return [p_.listItem].some(function (o) {
            return (o || {})[side]
        })
    }
    const location = { ...history.location }

    const renderTitle = () => {
        return (
            <div className={cn(p_.topContent ? styles.title : grid.p_md, !p_.topContent ? textStyles.center : '')}>
                <Typography type="title">{pane?.title}</Typography>
                {pane?.description && (
                    <Typography type="text" className={cn(grid.pt_micro, textStyles.mini)}>
                        {pane.description}
                    </Typography>
                )}
            </div>
        )
    }

    const renderDownloadForm = () => {
        return (
            <form
                onSubmit={(e) => {
                    e.preventDefault()

                    if (p_.onSubmit) {
                        p_.onSubmit(p_.selectedSide, 'download', {})
                    }

                    setState((prevState) => {
                        return { ...prevState, active: [] }
                    })
                }}
                className={cn(grid.colCenter, grid.p_md)}
            >
                <div className={cn(grid.w100, grid.mb_md, textStyles.center)}>{translate('downloadApprove')}</div>
                <div className={cn(grid.row, grid.justify, grid.mb_mini)}>
                    <Button mod={'withBorder'} onClick={cancelRemove} className={styles.button}>
                        {translate('no')}
                    </Button>
                    <Button type="submit" mod="fill" className={styles.button}>
                        {translate('yes')}
                    </Button>
                </div>
            </form>
        )
    }

    const renderMoveForm = () => {
        return (
            <form
                onSubmit={(e) => {
                    e.preventDefault()
                    setState((prevState) => {
                        return { ...prevState, active: [] }
                    })

                    if (p_.onSubmit) {
                        p_.onSubmit(p_.selectedSide, 'edit', s_.data)
                    }
                }}
                className={cn(grid.colCenter, grid.p_md, textStyles.center)}
            >
                <div className={cn(grid.w100, grid.mb_mini)}>
                    <Typography className={cn(grid.mb_mini, textStyles.up)} type="title">
                        {s_.editTitle}
                    </Typography>
                    <div className={grid.w100}>
                        <Dropdown
                            value={s_.data.newFolderId}
                            options={s_.folders}
                            placeholder={translate('chooseFolder')}
                            searchable
                            centered
                            type="tree"
                            onChange={(selected) =>
                                setState((prevState) => {
                                    return { ...prevState, data: { ...s_.data, newFolderId: selected.id } }
                                })
                            }
                            wrapperWidth={true}
                        />
                    </div>
                </div>
                <div className={cn(grid.w100, grid.mb_mini, styles.additionContent)}>
                    {isFolderChanged(s_) && isAdditionContentExist(p_)
                        ? pane?.additionContent?.map(({ title, name, type, options }: SidebarAdditionContent) => {
                              switch (type) {
                                  case 'checkbox':
                                      return (
                                          <div className={styles.additionContent__item} key={title}>
                                              <Checkbox
                                                  onClick={(checked: boolean) =>
                                                      setState((prevState) => {
                                                          return {
                                                              ...prevState,
                                                              data: { ...s_.data, [name]: checked },
                                                          }
                                                      })
                                                  }
                                                  className={cn(styles.additionContent__checkbox)}
                                                  labelClassName={cn(styles.additionContent__label)}
                                                  checked={Boolean(s_.data[name])}
                                                  label={title}
                                              />
                                          </div>
                                      )
                                  case 'radio':
                                      return (
                                          <div className={styles.additionContent__item} key={title}>
                                              <div className={styles.additionContent__title}>{title}</div>
                                              {options &&
                                                  isNotEmptyArray(options) &&
                                                  options.map(({ label, value }: { label: string; value: string }) => {
                                                      return (
                                                          <Radio
                                                              key={label}
                                                              className={cn(styles.additionContent__radio)}
                                                              label={label}
                                                              selected={s_.data[name] === value}
                                                              onClick={() =>
                                                                  setState((prevState) => {
                                                                      return {
                                                                          ...prevState,
                                                                          data: {
                                                                              ...s_.data,
                                                                              [name]: value,
                                                                          },
                                                                      }
                                                                  })
                                                              }
                                                          />
                                                      )
                                                  })}
                                          </div>
                                      )
                                  default:
                                      return null
                              }
                          })
                        : null}
                </div>
                {p_.topContent ? (
                    <div className={styles.formButtons}>
                        <Button
                            type="button"
                            mod="withBorder"
                            className={styles.button}
                            onClick={() => {
                                setState((prevState) => {
                                    return {
                                        ...prevState,
                                        active: [],
                                    }
                                })
                            }}
                        >
                            {translate('close')}
                        </Button>
                        <Button
                            type="submit"
                            mod="fill"
                            className={styles.button}
                            disabled={!(s_.data && s_.data.newFolderId)}
                        >
                            {translate('save')}
                        </Button>
                    </div>
                ) : (
                    <Button type="submit" mod="withBorder">
                        {translate('save')}
                    </Button>
                )}
            </form>
        )
    }

    const renderDeleteForm = () => {
        return (
            <form
                onSubmit={(e) => {
                    e.preventDefault()

                    if (p_.onSubmit) {
                        p_.onSubmit(p_.selectedSide, 'delete', {})
                    }

                    setState((prevState) => {
                        return { ...prevState, active: [] }
                    })
                }}
                className={cn(grid.colCenter, grid.p_md)}
            >
                <div className={cn(grid.w100, grid.mb_md, textStyles.center)}>{translate('deleteApprove')}</div>
                <div className={styles.formButtons}>
                    <Button type="button" mod="withBorder" className={styles.button} onClick={cancelRemove}>
                        {translate('no')}
                    </Button>
                    <Button type="submit" mod="fill" className={styles.button}>
                        {translate('yes')}
                    </Button>
                </div>
            </form>
        )
    }

    const hasOptionsLength = paneOptions.length > 0

    return (
        <div className={cn(styles.content)}>
            {pane && p_.selectedSide === p_.side && !isEmpty && (
                <div
                    className={cn(
                        p_.side === 'right' ? styles.contentWrapper : styles.wrapperBack,
                        p_.contentWrapperClassName
                    )}
                >
                    {pane.title && !p_.topContent && renderTitle()}
                    {p_.extraComponent && p_.extraComponent}
                    {p_.topContent && p_.topContent[p_.side] && <div>{p_.topContent[p_.side]}</div>}
                    {pane.title && p_.topContent && renderTitle()}
                    <div className={styles.toolbarListWrapper}>
                        {p_.selectFromFileMode && (
                            <SelectFromFileToolbarContent
                                selectedItems={paneOptions}
                                notSelectedItems={p_.notSelected}
                                onClearSelectFromFile={p_.onClearSelectFromFile}
                            />
                        )}
                        {showSearch && (
                            <div className={styles.searchWrapper}>
                                <EditText
                                    mod={'withBorder'}
                                    className={styles.selectedItemsSearch}
                                    placeholder={translate('search')}
                                    value={searchValue}
                                    onChange={(search: string) => {
                                        if (p_.onSearchChange) {
                                            p_.onSearchChange(search)
                                        }
                                        if (p_.searchValue === undefined) {
                                            setState((prevState) => {
                                                return {
                                                    ...prevState,
                                                    search,
                                                }
                                            })
                                        }
                                    }}
                                />
                                <Icon
                                    type={searchValue.length ? 'close' : 'search'}
                                    size={16}
                                    color={colors.grey}
                                    containerClass={styles.searchIcon}
                                    onClick={() => {
                                        if (p_.onSearchChange) {
                                            p_.onSearchChange('')
                                        }
                                        if (p_.searchValue === undefined) {
                                            setState((prevState) => {
                                                return {
                                                    ...prevState,
                                                    search: '',
                                                }
                                            })
                                        }
                                    }}
                                />
                            </div>
                        )}
                        {!p_.selectFromFileMode && (paneOptions.length > 0 || p_.onSearchChange) ? (
                            <div className={styles.optionsWrapper} onScroll={p_.onOptionsScroll}>
                                {paneOptions
                                    .filter((listItem) => {
                                        if (!useLocalSearch) return true

                                        const optionName = listItem.name ? listItem.name : listItem.title

                                        return (
                                            optionName && optionName.toLowerCase().includes(searchValue.toLowerCase())
                                        )
                                    })
                                    .map((listItem, index: number) => {
                                        const optionName = listItem.name ? listItem.name : listItem.title

                                        if (listItemSide(p_.side) && paneOptions.length === 1 && p_.listItem) {
                                            const List = p_.listItem[p_.side]

                                            return (
                                                <List
                                                    key={`${p_.side}Content__item_${index}`}
                                                    {...listItem}
                                                    {...p_.listItemProps}
                                                    downloadLink={pane.downloadLink}
                                                />
                                            )
                                        } else if (typeof listItem.id === 'number') {
                                            return (
                                                <>
                                                    <div
                                                        key={`${p_.side}Content__item_${index}`}
                                                        className={cn(grid.rowCenter, grid.noWrap, styles.listItem)}
                                                    >
                                                        <div>{getIcon(listItem, conflicts)}</div>
                                                        <div
                                                            className={styles.sidebarTopListItem}
                                                            onClick={() => topListItemClick(listItem)}
                                                        >
                                                            <div
                                                                className={cn(item.ellipsis, {
                                                                    [textStyles.redError]: conflicts.some(
                                                                        (conflict) => conflict.obj.id === listItem.id
                                                                    ),
                                                                })}
                                                            >
                                                                <DotTooltip
                                                                    tooltip={{
                                                                        html: optionName,
                                                                    }}
                                                                >
                                                                    <div
                                                                        className={cn(styles.optionName, item.ellipsis)}
                                                                    >
                                                                        {optionName}
                                                                    </div>
                                                                </DotTooltip>
                                                            </div>
                                                        </div>
                                                        <div className={grid.rowCenter}>
                                                            {pane.showEditIcon && (
                                                                <div className={grid.pl_nano}>
                                                                    <Tooltip title={translate('edit')} position="left">
                                                                        <CircleButton
                                                                            onClick={function () {
                                                                                if (p_.onEditItem) {
                                                                                    p_.onEditItem(listItem)
                                                                                }
                                                                            }}
                                                                        >
                                                                            <Icon type={'pencil'} size={10} />
                                                                        </CircleButton>
                                                                    </Tooltip>
                                                                </div>
                                                            )}
                                                            {!pane.hideDeleteIcon &&
                                                                (location.pathname === '/adBlocks/edit' ? (
                                                                    <div className={grid.pl_nano}>
                                                                        <Tooltip
                                                                            title={translate('deselect')}
                                                                            position="left"
                                                                        >
                                                                            <CircleButton
                                                                                onClick={() => {
                                                                                    setState((prevState) => {
                                                                                        return {
                                                                                            ...prevState,
                                                                                            visible: true,
                                                                                            index: index,
                                                                                        }
                                                                                    })
                                                                                }}
                                                                                type={'delete'}
                                                                            />
                                                                        </Tooltip>
                                                                    </div>
                                                                ) : (
                                                                    <div className={grid.pl_nano}>
                                                                        <Tooltip
                                                                            title={translate('deselect')}
                                                                            position="left"
                                                                        >
                                                                            <CircleButton
                                                                                onClick={() => {
                                                                                    let newList = [...paneOptions]
                                                                                    newList.splice(index, 1)

                                                                                    if (p_.onChange) {
                                                                                        p_.onChange(
                                                                                            newList,
                                                                                            p_.selectedSide
                                                                                        )
                                                                                    }
                                                                                }}
                                                                                type={'delete'}
                                                                            />
                                                                        </Tooltip>
                                                                    </div>
                                                                ))}
                                                        </div>
                                                    </div>
                                                    {location.pathname === '/adBlocks/edit' && s_.index === index && (
                                                        <div>
                                                            <form
                                                                onSubmit={(e) => {
                                                                    e.preventDefault()
                                                                    let newList = [...paneOptions]
                                                                    newList.splice(index, 1)

                                                                    if (p_.onChange) {
                                                                        p_.onChange(newList, p_.selectedSide)
                                                                    }
                                                                }}
                                                                className={
                                                                    s_.visible
                                                                        ? styles.formWrapper
                                                                        : styles.formWrapperDNone
                                                                }
                                                            >
                                                                <div className={styles.formTitle}>
                                                                    {translate('deleteSelectedContent')}?
                                                                </div>
                                                                <div className={styles.formButtons}>
                                                                    <Button
                                                                        indentSize="mini"
                                                                        type="submit"
                                                                        className={grid.mr_mini}
                                                                        mod="fill"
                                                                        onClick={() =>
                                                                            setState((prevState) => {
                                                                                return {
                                                                                    ...prevState,
                                                                                    visible: false,
                                                                                }
                                                                            })
                                                                        }
                                                                    >
                                                                        {translate('yes')}
                                                                    </Button>
                                                                    <Button
                                                                        indentSize="mini"
                                                                        onClick={() =>
                                                                            setState((prevState) => {
                                                                                return {
                                                                                    ...prevState,
                                                                                    visible: false,
                                                                                }
                                                                            })
                                                                        }
                                                                    >
                                                                        {translate('no')}
                                                                    </Button>
                                                                </div>
                                                            </form>
                                                        </div>
                                                    )}
                                                </>
                                            )
                                        } else {
                                            return null
                                        }
                                    })}
                            </div>
                        ) : null}
                    </div>
                    {p_.isShowActions && p_.side === 'right' && (
                        <DropMenu
                            active={s_.active}
                            onChange={(active) => {
                                if (active.includes('edit')) {
                                    p_.onGetFoldersOnMultiple()
                                }
                                setState((prevState) => {
                                    return {
                                        ...prevState,
                                        active,
                                    }
                                })
                            }}
                            mod="centered"
                            className={item.blackOpacity}
                            items={getItemActions({
                                isDeletable: p_.isDeletable,
                                isEditable: p_.isEditable,
                                isDownloadable: p_.isDownloadable,
                            })}
                        />
                    )}
                    {p_.bottomContent && p_.bottomContent[p_.side] && <div>{p_.bottomContent[p_.side]}</div>}
                </div>
            )}
            {p_.customContent && p_.customContent[p_.side] && (!isEmpty || p_.showAlwaysCustomContent) && (
                <div className={cn(p_.side === 'right' ? styles.contentWrapper : styles.wrapperBack)}>
                    {p_.customContent[p_.side]}
                </div>
            )}
            {isEmpty && emptyText && (
                <div className={cn(p_.side === 'right' ? styles.contentWrapper : styles.wrapperBack)}>
                    <div className={cn(styles.empty)}>
                        <div className={`${grid.colCenter} ${grid.w100}`}>
                            {typeof emptyText.title === 'object' && <div>{emptyText.title}</div>}
                            {typeof emptyText.title === 'string' && (
                                <Typography type="title" className={cn(textStyles['normalPlus'], textStyles.up)}>
                                    {emptyText.title}
                                </Typography>
                            )}
                            <div className={cn(styles.description, !emptyText.title ? styles.descriptionOnly : '')}>
                                {emptyText.wiki && (
                                    <WikiInfo
                                        blockKey={emptyText.wiki.blockKey}
                                        blockSubKey={emptyText.wiki.blockSubKey}
                                        infoKey={emptyText.wiki.infoKey}
                                    />
                                )}
                                <p>{emptyText.description}</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <Modal
                open={Boolean(p_.topContent && s_.active.length === 1 && s_.active.includes('edit'))}
                mod={'black'}
                showCloseIcon={false}
            >
                <div className={styles.modalWrapper}>{renderMoveForm()}</div>
            </Modal>
            <Modal
                open={Boolean(p_.topContent && s_.active.length === 1 && s_.active.includes('delete'))}
                mod={'black'}
                showCloseIcon={false}
            >
                <div className={styles.modalWrapper}>{renderDeleteForm()}</div>
            </Modal>
            <Modal
                open={Boolean(p_.topContent && s_.active.length === 1 && s_.active.includes('download'))}
                mod={'black'}
                showCloseIcon={false}
            >
                <div className={styles.modalWrapper}>{renderDownloadForm()}</div>
            </Modal>
        </div>
    )
}

Content.defaultProps = {
    conflicts: [],
    onEditItem: () => {},
}

export default Sidebar
